import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  CircularProgress
} from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useNetwork } from '../../../../../utils/network';
import { useShowSnackbar } from '../../../../../utils/snackbar';
import { stripePromise } from '../../../../../utils/stripe';
import {
  billingPromptDismissed,
  dismissBillingPrompt
} from '../../../../../utils/billingPromptDismissal';
import { UpdateCardForm } from '../../../UserSettings/components/UpdateCardForm';
import {
  dialogPaperSx,
  primaryBtnSx,
  ghostBtnSx,
  panelSx,
  PT
} from '../../../../../components/portalUi';

/* Asks a subscriber whose billing is still on the OLD Stripe account to move it.
 *
 * WHO SEES IT. Only a member `GET user/subscription/migration-status` says
 * `needsMigration` for, which is answered by the SAME function the migrate endpoint
 * obeys (`assessLegacyMigration`): a live subscription on the legacy account, not
 * already cancelling, not renewing within the hour. So it appears only when pressing
 * the button will actually work — a prompt that appears and then refuses is how a
 * migration campaign turns into a support queue — and it renders nothing at all for
 * everybody else, including every member already on the new account.
 *
 * IT KEEPS ASKING UNTIL THEY MOVE. The modal opens at every sign-in, and "Not now"
 * only quiets it for the rest of that one (see utils/billingPromptDismissal). In
 * between, the dashboard's quick-actions button lists "Update payment details" and
 * counts it in its badge — the same place a member already looks for packs to open
 * and a faucet to claim — and that item reopens this (`openSignal`). The dashboard
 * learns whether to show it from `onNeedsMigrationChange`, so it never asks the
 * server itself. Both stop the moment the move succeeds: migrating sets the old
 * subscription to cancel at its period end, so migration-status stops saying
 * `needsMigration` and this renders nothing from then on.
 *
 * It is still dismissible rather than a wall: nothing breaks if a member ignores it,
 * their subscription keeps billing on the legacy account exactly as before.
 *
 * The endpoint also returns a `reason` when it says no, which is what lets this stay
 * QUIET for a member who is simply leaving while still telling a member with an
 * unpaid invoice to go and fix their card — two very different people who would
 * otherwise see the same nothing.
 */

type Payment = { clientSecret: string; publishableKey: string };

export const MigrateBillingPrompt = ({
  /* The dashboard opens its modals in sequence — 2FA, then referral, then this.
   * Three at once would be three things to dismiss before reaching the page. */
  enabled,
  /* Bumped by the dashboard's quick-actions item to reopen this on demand — the
   * same signal pattern as that menu's "Claim faucet". Only a CHANGE opens it. */
  openSignal = 0,
  /* Reports whether this member still needs to move, so the quick-actions menu can
   * list the item and count it in its badge without asking the server a second
   * time. Called with false once the move succeeds. */
  onNeedsMigrationChange,
  /* DEV ONLY (App.tsx /preview/migrate-billing). Opens the dialog without asking
   * the server, because the real trigger cannot fire locally: it needs
   * assessLegacyMigration to find a LEGACY subscription in Stripe, and local runs
   * on test keys. Purely so the thing can be looked at. */
  previewOpen = false
}: {
  enabled: boolean;
  openSignal?: number;
  onNeedsMigrationChange?: (needs: boolean) => void;
  previewOpen?: boolean;
}) => {
  const { get, post } = useNetwork();
  const { showSnackbar } = useShowSnackbar();
  const [needsMigration, setNeedsMigration] = useState(previewOpen);
  const [open, setOpen] = useState(previewOpen);
  const [reason, setReason] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [payment, setPayment] = useState<Payment | null>(null);

  /* Memoised, and matching SettingsUserPayments exactly. loadStripe() inside the
   * render body would hand <Elements> a NEW promise on every render, which
   * remounts the card field and loses whatever the member had typed. Falls back
   * to the primary key so the prop is never undefined — the server always sends
   * the key for the account that issued THIS SetupIntent, and Stripe.js rejects
   * a key that does not match the client secret. */
  const stripeForForm = useMemo(
    () =>
      payment?.publishableKey
        ? loadStripe(payment.publishableKey)
        : stripePromise,
    [payment?.publishableKey]
  );

  useEffect(() => {
    if (previewOpen) return; // nothing to ask; it is already open
    if (!enabled) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await get('user/subscription/migration-status', true);
        if (cancelled) return;
        setReason(res?.reason ?? null);
        const needs = res?.needsMigration === true;
        setNeedsMigration(needs);
        onNeedsMigrationChange?.(needs);
        /* Asked at every sign-in; "Not now" holds only for the rest of this one.
         * The quick-actions item stays listed either way. */
        if (needs && !billingPromptDismissed()) setOpen(true);
      } catch {
        /* Silent on purpose. This is an optional nudge; a member should never
         * see an error about something they did not ask for. */
      }
    })();
    return () => {
      cancelled = true;
    };
    /* `get` is a fresh function on every render (useNetwork does not memoise it),
     * so listing it here re-ran this effect — and its request — on every single
     * dashboard re-render. For a legacy subscriber each of those asks Stripe about
     * their subscription. The trigger is `enabled`, and that is what belongs here. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, previewOpen]);

  /* The quick-actions item asked for it. Keyed on the signal alone: re-running when
   * needsMigration flips would reopen a prompt the member already closed, the next
   * time the status happened to be re-read. */
  useEffect(() => {
    if (openSignal > 0 && needsMigration) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSignal]);

  const dismiss = () => {
    dismissBillingPrompt();
    setOpen(false);
    setPayment(null);
  };

  const start = async () => {
    setStarting(true);
    try {
      // migration: true collects the card on the NEW account, which is where the
      // replacement subscription and the card have to live.
      const res = await post(
        'user/payment-method/setup-intent',
        { migration: true },
        true
      );
      if (!res?.success || !res?.clientSecret) {
        showSnackbar(res?.message || 'Could not start the update.', 'error');
        return;
      }
      setPayment({
        clientSecret: res.clientSecret,
        publishableKey: res.publishableKey
      });
    } catch {
      showSnackbar('Could not start the update. Please try again.', 'error');
    } finally {
      setStarting(false);
    }
  };

  /* An unpaid invoice is the one refusal worth speaking up about: that member's
   * card is already failing on their CURRENT subscription, and if nobody tells
   * them, Stripe gives up and they lose it. Everyone else the endpoint refuses —
   * already cancelling, renewing within the hour, not on legacy at all — needs
   * to hear nothing. */
  if (!open && reason === 'unpaid') {
    return (
      <Box
        sx={{
          ...panelSx,
          mb: 2,
          p: 2,
          borderColor: 'rgba(255,107,107,0.4)'
        }}
      >
        <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
          There is a problem with your card
        </Typography>
        <Typography variant="body2" color="text.secondary">
          A payment on your subscription did not go through. Update your card
          under Settings → Payments to keep it active.
        </Typography>
      </Box>
    );
  }

  // Nobody to ask: on the new account already, or moved just now.
  if (!needsMigration) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={dismiss}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            We&apos;ve switched payment providers
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please update your payment details to keep your subscription
            running. It takes a moment, and nothing else about your plan
            changes.
          </Typography>

          {/* The three things a member actually wants to know before typing a card
              number, and all three are guarantees the server enforces rather than
              copy we hope is true. */}
          <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 2.5 }}>
            {[
              'You are not charged today.',
              'Your next payment stays on your existing renewal date, for the same amount.',
              'Your giveaway entries carry over in full — nothing resets.'
            ].map((line) => (
              <Typography
                component="li"
                variant="body2"
                key={line}
                sx={{ mb: 0.75 }}
              >
                {line}
              </Typography>
            ))}
          </Box>

          {payment ? (
            <Elements
              stripe={stripeForForm}
              options={{
                clientSecret: payment.clientSecret,
                // Same appearance as the Payments tab — one card field, two places.
                appearance: {
                  theme: 'night',
                  variables: {
                    colorPrimary: PT.teal,
                    colorBackground: PT.surface,
                    colorText: PT.text,
                    borderRadius: '10px',
                    fontFamily: 'Montserrat, sans-serif'
                  }
                }
              }}
            >
              <UpdateCardForm
                mode="migrate"
                onDone={() => {
                  /* Moved. The server stops reporting needsMigration from here on
                   * (the old subscription is now set to cancel at its period end),
                   * so this is only about the current page: drop the modal and the
                   * quick-actions item without waiting for a re-fetch. */
                  setOpen(false);
                  setPayment(null);
                  setNeedsMigration(false);
                  onNeedsMigrationChange?.(false);
                }}
                onCancel={() => setPayment(null)}
              />
            </Elements>
          ) : (
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                justifyContent: 'flex-end',
                mt: 1,
                /* Stacked on a phone, where "Update payment details" and "Not now"
                   side by side wrap to two lines each and read as four buttons.
                   Reversed so the action a member came for is the one under their
                   thumb. */
                flexDirection: { xs: 'column-reverse', sm: 'row' }
              }}
            >
              <Button
                sx={{ ...ghostBtnSx, whiteSpace: 'nowrap' }}
                onClick={dismiss}
                disabled={starting}
              >
                Not now
              </Button>
              <Button
                sx={{ ...primaryBtnSx, whiteSpace: 'nowrap' }}
                onClick={start}
                disabled={starting}
              >
                {starting ? (
                  <CircularProgress size={20} />
                ) : (
                  'Update payment details'
                )}
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
